import React, { useEffect, useRef } from 'react';
import { Users } from 'lucide-react';
import { useCommunityPosts } from '../hooks/useCommunityPosts';
import { COMMUNITY_CONFIG } from '../config/communityConfig';
import { formatDateShort } from '../utils/dates';
import { trackCommunityPostEvent } from '../services/analyticsService';

function PostCard({ post, userId, impressionSet }) {
  useEffect(() => {
    if (!post?.id) return;
    if (!impressionSet?.current) return;
    if (impressionSet.current.has(post.id)) return;
    impressionSet.current.add(post.id);
    trackCommunityPostEvent({
      event_type: 'impression',
      post_id: post.id,
      brand_name: post.brandName,
      linked_offer_id: post.linkedOfferId,
      user_id: userId,
      page_context: 'community',
    });
  }, [post?.id, post?.brandName, post?.linkedOfferId, impressionSet, userId]);

  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-semibold text-lg leading-snug">{post.title}</h3>
          <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-2">
            {post.postType ? (
              <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                {COMMUNITY_CONFIG.postTypeLabels[post.postType] || post.postType}
              </span>
            ) : null}
            {post.category ? (
              <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">{post.category}</span>
            ) : null}
            {post.publishAt ? <span>Published {formatDateShort(post.publishAt)}</span> : null}
          </div>
        </div>
      </div>

      {post.imageUrl ? (
        <div className="mt-3">
          <img
            src={post.imageUrl}
            alt=""
            className="w-full h-44 rounded-lg object-cover bg-gray-100"
            loading="lazy"
          />
        </div>
      ) : null}

      {post.body ? <p className="text-gray-700 text-sm mt-3 whitespace-pre-wrap break-words">{post.body}</p> : null}

      {post.ctaLink ? (
        <a
          href={post.ctaLink}
          target="_blank"
          rel="noreferrer"
          onClick={() => {
            trackCommunityPostEvent({
              event_type: 'click',
              post_id: post.id,
              brand_name: post.brandName,
              linked_offer_id: post.linkedOfferId,
              user_id: userId,
              page_context: 'community',
            });
          }}
          className="mt-3 inline-flex items-center justify-center w-full px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700"
        >
          {post.ctaText || COMMUNITY_CONFIG.defaultCtaText}
        </a>
      ) : null}
    </div>
  );
}

const CommunityFeedView = ({ user, onManagePosts }) => {
  const { posts, status, errorMessage } = useCommunityPosts('published');
  const impressionSet = useRef(new Set());
  const userId = user?.id ?? null;

  // Temporary debug logging
  console.log('[community] user role:', user?.role, 'user id:', user?.id);

  const canManage = user?.role === 'admin' || user?.role === 'editor';

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold mb-2">Community</h2>
          <p className="text-gray-600">Curated updates, inspiration, and challenges from the team.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {canManage ? (
            <button
              type="button"
              onClick={onManagePosts}
              className="px-4 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-50"
            >
              Manage Posts
            </button>
          ) : null}
          <div className="p-2 bg-gray-100 rounded-lg">
            <Users className="h-6 w-6 text-gray-700" />
          </div>
        </div>
      </div>

      {status === 'loading' ? (
        <div className="text-sm text-gray-600">Loading feed…</div>
      ) : status === 'error' ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4">
          <p className="text-sm font-medium text-red-800">{COMMUNITY_CONFIG.errorFeedText}</p>
          {errorMessage ? <p className="text-xs text-red-700 mt-1">{errorMessage}</p> : null}
        </div>
      ) : posts.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-600">{COMMUNITY_CONFIG.emptyFeedText}</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {posts.map((p) => (
            <PostCard key={p.id} post={p} userId={userId} impressionSet={impressionSet} />
          ))}
        </div>
      )}
    </div>
  );
};

export default CommunityFeedView;

